"use client";

import type { FC, Ref } from "react";
import {
  Card,
  type ChildrenProp,
  Flex,
  Grid,
  Placeholder,
  Text,
} from "@huub-web/ui";
import { rem } from "@huub-web/utils";

import { LoaderStep, type LoaderStepProps } from "./components/LoaderStep";

import { contentStyle } from "./AnalysisLoader.css";

export type AnalysisLoaderProps = ChildrenProp & {
  id?: string;
  /**
   * The title to display
   */
  title?: string;
  /**
   * The description to display
   */
  description?: string;
  /**
   * The variant to display
   */
  variant?: "dynamic" | "placeholder";
  /**
   * Whether to display an error
   */
  error?: boolean;
  /**
   * The checks to display
   */
  steps?: LoaderStepProps[];
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * AnalysisLoader component to display a loading state with a title, description, and steps.
 */
export const AnalysisLoader: FC<AnalysisLoaderProps> = ({
  title,
  description,
  variant = "dynamic",
  error = false,
  steps: stepsProp,
  children,
  ref,
  ...props
}) => {
  const totalSteps = stepsProp?.length ?? 0;

  const currentStep = (() => {
    const current =
      (stepsProp?.filter((step) => step.status === "completed").length ?? 0) +
      1;

    if (current > totalSteps) {
      return stepsProp?.length;
    }

    return current;
  })();

  const steps: LoaderStepProps[] = (() => {
    const inProgressStep = stepsProp?.find(
      (step) => step.status === "in-progress",
    );

    if (!inProgressStep) {
      const completedStep = stepsProp?.find(
        (step) => step.status === "completed",
      );

      let waiting = false;

      return stepsProp?.map((step) => {
        if (step === completedStep) {
          waiting = true;
          return step;
        }

        if (waiting) {
          waiting = false;
          return {
            ...step,
            status: "waiting",
          };
        }

        return step;
      }) as LoaderStepProps[];
    }

    return stepsProp ?? [];
  })();

  return (
    <Flex ref={ref} direction="column" gap="s" align="center" {...props}>
      {variant === "dynamic" && !error && (
        <Card
          border="default"
          overflowHidden
          edgeDirection="horizontal"
          padding="s"
        >
          <Grid variant="twoColumn">
            <Flex direction="column" gap="s" grow className={contentStyle()}>
              <Text variant="bodyL" bold>
                {title ??
                  (!!currentStep && !!totalSteps
                    ? `Bezig met analyseren (${currentStep}/${totalSteps})`
                    : "Bezig met analyseren")}
              </Text>

              {description && (
                <Text markdown color="currentColor50">
                  {description}
                </Text>
              )}

              {children}
            </Flex>

            {steps && steps.length > 0 && (
              <Card variant="tertiary">
                <Flex as="ul" direction="column" gap="xs">
                  {steps.map((step, i) => (
                    <LoaderStep key={i} {...step} />
                  ))}
                </Flex>
              </Card>
            )}
          </Grid>

          {/* <ProgressBar
            variant={isNumber(progress) ? "default" : "infinite"}
            className={progressBarStyle}
            position="absolute"
            align="bottom"
            progress={progress}
          /> */}
        </Card>
      )}

      {variant === "placeholder" && !error && (
        <Flex direction="column" gap="s" style={{ width: "100%" }}>
          <Placeholder height={rem(80)} variant="loading" />
          <Placeholder height={rem(80)} variant="loading" />
          <Placeholder height={rem(80)} variant="loading" />
        </Flex>
      )}

      {error && (
        <Card border="default" overflowHidden edgeDirection="horizontal">
          <Flex className={contentStyle()} direction="column" gap="s" grow>
            <Text variant="bodyL" bold>
              {title}
            </Text>
            <Text markdown>{description}</Text>
          </Flex>
        </Card>
      )}
    </Flex>
  );
};
